import { LegalDocument } from '@westhetics/legal/react'
import { Button, EmptyState, PageTitle, StatusTag, Surface, toast } from '@westhetics/ui'
import { useId } from 'react'
import { useParams } from 'react-router'
import { LoadError } from '@/app/load-error'
import { BackLink, ReadingSkeleton, noteText } from '@/app/shell'
import { errorMessage } from '@/lib/errors'
import { formatDateTimeIn } from '@/lib/format'
import { routes } from '@/routes'
import { openSignedUrl, useDocument, usePdfLink } from './api'

/**
 * O documento (docs/areas/portal-do-paciente.md, "Documento"; RN-POR-06): o mesmo Markdown que a
 * clínica guarda, renderizado por `@westhetics/legal`, e o PDF protegido para baixar. Arquétipo:
 * leitura. Documento importado sem Markdown mostra só o PDF; sem PDF, a nota diz que a cópia está
 * com a clínica.
 */

const PDF_NOTE =
  'Este documento foi preparado pela clínica com os seus dados. O PDF é protegido: permite ler e imprimir, não copiar o texto.'
const NO_PDF = 'A cópia deste documento está com a clínica.'

export function DocumentPage() {
  const { id = '' } = useParams()
  const query = useDocument(id)
  const pdf = usePdfLink()
  const reasonId = useId()

  if (query.isPending) return <ReadingSkeleton label="Carregando o documento" />
  if (query.isError) {
    return (
      <div className="flex flex-col gap-4">
        <BackLink to={routes.home}>Voltar ao início</BackLink>
        <LoadError
          title="Não conseguimos abrir este documento"
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      </div>
    )
  }

  const doc = query.data
  const { date } = formatDateTimeIn(doc.generatedAt, doc.clinic.timezone)
  return (
    <div className="flex flex-col gap-6">
      <BackLink to={routes.appointment(doc.appointmentId)}>Voltar ao atendimento</BackLink>
      <PageTitle
        eyebrow={`Documento · ${doc.clinic.name}`}
        title={doc.name}
        meta={[
          `gerado em ${date}`,
          doc.version ? `versão ${doc.version}` : null,
          doc.shortHash ? `hash ${doc.shortHash}` : null,
        ]
          .filter(Boolean)
          .join(' · ')}
        trailing={<StatusTag level={doc.level}>{doc.label}</StatusTag>}
      />

      <div className="flex flex-col items-start gap-2">
        <Button
          variant="outline"
          disabled={!doc.hasPdf}
          aria-describedby={doc.hasPdf ? undefined : reasonId}
          loading={pdf.isPending}
          loadingLabel="Preparando…"
          onClick={() =>
            pdf.mutate(doc.id, {
              onSuccess: (link) => openSignedUrl(link.url),
              onError: (error) => toast(errorMessage(error)),
            })
          }
        >
          Baixar PDF
        </Button>
        {!doc.hasPdf ? (
          <p id={reasonId} className={noteText}>
            {NO_PDF}
          </p>
        ) : null}
      </div>

      {doc.resolvedMarkdown ? (
        <Surface padding="none" className="max-desk:px-4 px-6 py-6">
          <LegalDocument markdown={doc.resolvedMarkdown} clinicName={doc.clinic.name} />
        </Surface>
      ) : (
        <EmptyState
          title="Este documento veio do sistema anterior"
          description={
            doc.hasPdf
              ? 'A leitura na tela não está disponível para ele. Baixe o PDF para ler.'
              : NO_PDF
          }
        />
      )}

      <p className={noteText}>{PDF_NOTE}</p>
    </div>
  )
}
